import { Button, Input } from '@design-systems/canvas-ui'
import { renderControl } from './centralControlBuilder'
import { EXCLUDED_TABLE_CONTROL_TYPES } from '../../common/commonConstants'
import {
  getTableControlLabel,
  getTableSectionTitle,
  getTableAddButtonLabel,
  getTableDeleteButtonLabel,
  getTableControls,
  getTableRows,
  getTableDropdownOptions,
  buildTableControlMetadata,
  buildTableCellControl,
  shouldDisableTableDeleteButton,
} from '../../common'

const DynamicTableSection = ({
  sectionTemplate,
  controls = [],
  sectionData = [],
  sectionPath,
  rfiMasterData,
  isReadonly = false,
  errorCache,
  onInputChange,
  onAddRow,
  onDeleteRow,
  setIsTriggerSave,
}) => {
  const tableControls = getTableControls(controls, EXCLUDED_TABLE_CONTROL_TYPES)
  const rows = getTableRows(sectionData)

  const sectionTitle = getTableSectionTitle(sectionTemplate)
  const showActionsColumn = Boolean(sectionTemplate?.remove) && !isReadonly
  const showAddButton = Boolean(sectionTemplate?.add_more) && !isReadonly

  const renderTableCell = ({ control, rowData, rowIndex }) => {
    const dropdownOptions = getTableDropdownOptions({
      control,
      rfiMasterData,
    })
    const metadata = buildTableControlMetadata({
      control,
      rowData,
      rowIndex,
      sectionPath,
      sectionTemplate,
      errorCache,
      isReadonly,
      dropdownOptions,
    })

    const tableCellControl = buildTableCellControl({
      control,
      metadata,
    })

    return renderControl(
      tableCellControl,
      rowData || {},
      rfiMasterData,
      isReadonly
        ? undefined
        : (value, updatedMetadata) => {
            onInputChange?.(value, updatedMetadata || metadata, rowIndex)
          },
    )
  }

  const handleDeleteRow = (rowIndex) => {
    const isDisabled = shouldDisableTableDeleteButton({
      rows,
      rowIndex,
      sectionTemplate,
    })

    if (isDisabled) return

    onDeleteRow?.(rowIndex)
  }

  return (
    <div
      className="dynamic-table-section"
      onMouseEnter={() => setIsTriggerSave?.(false)}
      onMouseLeave={() => setIsTriggerSave?.(true)}
    >
      {sectionTitle && (
        <Input.Label className="dynamic-table-section-title hc8-clr-base">
          {sectionTitle}
        </Input.Label>
      )}

      <div className="dynamic-table-scroll">
        <table className="dynamic-table">
          <thead>
            <tr>
              {tableControls.map((control) => (
                <th key={`header-${control.uid}`} className="hc8-clr-base">
                  {getTableControlLabel(control)}
                  {control?.is_mandatory && (
                    <span className="hc8-clr-error">{' *'}</span>
                  )}
                </th>
              ))}

              {showActionsColumn && <th className="hc8-clr-base">Actions</th>}
            </tr>
          </thead>

          <tbody>
            {rows.map((rowData, rowIndex) => (
              <tr key={`row-${rowIndex}`}>
                {tableControls.map((control) => (
                  <td key={`cell-${rowIndex}-${control.uid}`}>
                    <div className="dynamic-table-cell-content">
                      {renderTableCell({
                        control,
                        rowData,
                        rowIndex,
                      })}
                    </div>
                  </td>
                ))}

                {showActionsColumn && (
                  <td className="dynamic-table-action-cell">
                    <div className="dynamic-table-action-content">
                      <Button
                        data-testid={`btn-delete-row-${rowIndex}`}
                        variant="secondary"
                        className="dynamic-table-button"
                        isDisabled={shouldDisableTableDeleteButton({
                          rows,
                          rowIndex,
                          sectionTemplate,
                        })}
                        onClick={() => handleDeleteRow(rowIndex)}
                      >
                        {getTableDeleteButtonLabel(sectionTemplate)}
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAddButton && (
        <div className="dynamic-table-add-button-wrapper">
          <Button
            data-testid={`btn-add-row-${sectionTemplate?.uid}`}
            variant="primary"
            className="dynamic-table-button"
            onClick={onAddRow}
          >
            {getTableAddButtonLabel(sectionTemplate)}
          </Button>
        </div>
      )}
    </div>
  )
}

export default DynamicTableSection
